import { Tooltip as TooltipPrimitive } from '@base-ui/react';
import { cn, type Merge } from '@sohanemon/utils';
import * as React from 'react';

const TooltipProvider = ({
	delayDuration,
	delay,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider> & {
	delayDuration?: number;
}) => <TooltipPrimitive.Provider delay={delayDuration ?? delay} {...props} />;

const Tooltip = TooltipPrimitive.Root;

// Supports Radix-style asChild via Base UI's render prop
const TooltipTrigger = ({
	asChild,
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger> & {
	asChild?: boolean;
}) => {
	if (asChild && React.isValidElement(children)) {
		return <TooltipPrimitive.Trigger render={children} {...props} />;
	}
	return (
		<TooltipPrimitive.Trigger {...props}>{children}</TooltipPrimitive.Trigger>
	);
};

type TooltipContentProps = {
	sideOffset?: number;
	side?: React.ComponentProps<typeof TooltipPrimitive.Positioner>['side'];
	align?: React.ComponentProps<typeof TooltipPrimitive.Positioner>['align'];
	className?: string;
	children?: React.ReactNode;
} & Omit<
	React.ComponentProps<typeof TooltipPrimitive.Popup>,
	'className' | 'children'
>;

const TooltipContent = ({
	className,
	sideOffset = 4,
	side = 'top',
	align = 'center',
	children,
	...props
}: TooltipContentProps) => (
	<TooltipPrimitive.Portal>
		<TooltipPrimitive.Positioner
			side={side}
			align={align}
			sideOffset={sideOffset}
			className="isolate z-50"
		>
			<TooltipPrimitive.Popup
				className={cn(
					'fade-in-0 zoom-in-95 data-closed:fade-out-0 data-closed:zoom-out-95',
					'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
					'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
					'z-50 animate-in overflow-hidden rounded-md border border-border/40',
					'bg-popover px-3 py-1.5 text-popover-foreground text-sm shadow-md shadow-shadow-popover',
					'wrap-break-word data-closed:animate-out',
					className,
				)}
				{...props}
			>
				{children}
				<TooltipPrimitive.Arrow className="data-[side=bottom]:top-[-5px] data-[side=left]:right-[-5px] data-[side=top]:bottom-[-5px] data-[side=right]:left-[-5px]">
					<div className="size-2.5 rotate-45 border border-border/40 bg-popover" />
				</TooltipPrimitive.Arrow>
			</TooltipPrimitive.Popup>
		</TooltipPrimitive.Positioner>
	</TooltipPrimitive.Portal>
);

const WithTooltip = ({
	content,
	contentProps: { className: contentClassName = '', ...contentProps } = {},
	className,
	offset,
	side,
	open,
	...props
}: Merge<
	React.ComponentProps<typeof TooltipTrigger>,
	{
		content: React.ReactNode;
		offset?: number;
		side?: React.ComponentProps<typeof TooltipPrimitive.Positioner>['side'];
		contentProps?: Omit<
			React.ComponentProps<typeof TooltipContent>,
			'children'
		>;
		delay?: number;
		open?: boolean;
	}
>) => {
	if (content)
		return (
			<Tooltip open={open}>
				<TooltipTrigger
					render={<span className={cn('group', className)} />}
					{...props}
				/>
				<TooltipContent
					{...contentProps}
					className={cn('max-w-sm', contentClassName)}
					sideOffset={offset}
					side={side}
				>
					{typeof content === 'string' ? <p>{content}</p> : content}
				</TooltipContent>
			</Tooltip>
		);

	return props.children;
};

export {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
	WithTooltip,
};
